class BogosSelector extends HTMLElement {
  #data = { variants: {}, flavors: {} };
  #flavor = '';
  #options = [];
  #group = null;
  #button = null;
  #autoship = null;
  #status = null;
  #observer = null;
  #syncQueued = false;

  connectedCallback() {
    this.#data = this.#parseData();
    this.#flavor = this.dataset.initialFlavor ?? '';
    this.#group = this.querySelector('[role="radiogroup"]');
    this.#options = [...this.querySelectorAll('[data-bogos-option]')];
    this.#button = this.querySelector('[data-bogos-atc]');
    this.#autoship = this.querySelector('[data-bogos-autoship]');
    this.#status = this.querySelector('[data-bogos-status]');

    this.#group?.addEventListener('click', this.#handleOptionClick);
    this.#group?.addEventListener('keydown', this.#handleOptionKeydown);
    this.#button?.addEventListener('click', this.#handleAddToCart);
    this.#autoship?.addEventListener('change', this.#render);
    document.addEventListener('click', this.#scheduleSync);
    document.addEventListener('change', this.#scheduleSync);
    this.#observer = new MutationObserver(this.#scheduleSync);
    this.#observer.observe(document.body, { subtree: true, attributes: true, attributeFilter: ['value'] });
    this.#syncFlavor();
  }

  disconnectedCallback() {
    this.#group?.removeEventListener('click', this.#handleOptionClick);
    this.#group?.removeEventListener('keydown', this.#handleOptionKeydown);
    this.#button?.removeEventListener('click', this.#handleAddToCart);
    this.#autoship?.removeEventListener('change', this.#render);
    document.removeEventListener('click', this.#scheduleSync);
    document.removeEventListener('change', this.#scheduleSync);
    this.#observer?.disconnect();
  }

  #parseData() {
    try {
      return JSON.parse(this.querySelector('[data-bogos-data]')?.textContent ?? '{}');
    } catch {
      return { variants: {}, flavors: {} };
    }
  }

  get #entry() {
    return this.#data.flavors?.[this.#flavor];
  }

  get #selectedIndex() {
    return Math.max(
      this.#options.findIndex((option) => option.getAttribute('aria-checked') === 'true'),
      0
    );
  }

  #scheduleSync = () => {
    if (this.#syncQueued) return;

    this.#syncQueued = true;
    requestAnimationFrame(() => {
      this.#syncQueued = false;
      this.#syncFlavor();
    });
  };

  #syncFlavor = () => {
    this.#setFlavor(this.#currentFlavor());
  };

  #setFlavor(flavor) {
    if (!flavor || flavor === this.#flavor) return;

    this.#flavor = flavor;
    this.#render();
  }

  #currentFlavor() {
    const { variants = {}, flavors = {} } = this.#data;
    const input = document.querySelector(
      `input[name="id"][data-productid="${this.dataset.productId}"]`
    );
    const variantId = input?.value || new URLSearchParams(window.location.search).get('variant');
    const flavor = variants[variantId];

    return flavor && flavors[flavor] ? flavor : this.#flavor;
  }

  #render = () => {
    const entry = this.#entry;
    if (!entry) return;

    const withSubscription = this.#autoship?.checked ?? false;

    this.#options.forEach((option, index) => {
      const tier = entry.tiers?.[index];
      if (!tier) return;

      const each = option.querySelector('[data-bogos-each]');
      const off = option.querySelector('[data-bogos-off]');
      const compare = option.querySelector('[data-bogos-compare]');
      const compareValue = option.querySelector('[data-bogos-compare-value]');

      if (each) each.textContent = withSubscription ? tier.eachSub ?? tier.each : tier.each;
      if (off) {
        off.textContent = tier.off;
        off.hidden = !tier.off;
      }
      if (compare) compare.hidden = !tier.compare;
      if (compareValue) compareValue.textContent = tier.compare;
    });

    this.#updateButton(entry.available);
  };

  #updateButton(available) {
    if (!this.#button) return;

    this.#button.disabled = !available;
    this.#button.textContent = available ? this.dataset.labelAdd : this.dataset.labelSoldOut;
  }

  #handleOptionClick = (event) => {
    const option = event.target.closest('[data-bogos-option]');
    if (!option) return;

    this.#select(this.#options.indexOf(option));
  };

  #handleOptionKeydown = (event) => {
    const step = { ArrowDown: 1, ArrowRight: 1, ArrowUp: -1, ArrowLeft: -1 }[event.key];
    if (!step) return;

    event.preventDefault();
    const total = this.#options.length;
    const next = (this.#selectedIndex + step + total) % total;
    this.#select(next);
    this.#options[next]?.focus();
  };

  #select(index) {
    if (index < 0) return;

    this.#options.forEach((option, optionIndex) => {
      const selected = optionIndex === index;
      option.setAttribute('aria-checked', String(selected));
      option.tabIndex = selected ? 0 : -1;
    });
  }

  #handleAddToCart = async () => {
    if (this.#button.disabled || this.#button.getAttribute('aria-busy') === 'true') return;

    this.#syncFlavor();
    const entry = this.#entry;
    if (!entry?.available) return;

    const index = this.#selectedIndex;
    const quantity = Number(this.#options[index]?.dataset.quantity) || 1;
    const free = Number(this.#options[index]?.dataset.free) || 0;
    const code = entry.tiers?.[index]?.code ?? '';
    const isOffer = index > 0;

    this.#setBusy(true);
    this.#setStatus('');

    try {
      const sections = this.#cartSectionIds();
      const response = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          items: this.#buildCartItems(entry, quantity, free),
          ...(sections.length && { sections: sections.join(',') }),
        }),
      });
      const data = await response.json();

      if (!response.ok || data.status) {
        this.#setStatus(data.description ?? data.message ?? this.dataset.errorMessage);
        return;
      }

      if (isOffer) await this.#clearDiscounts();

      const cart = isOffer && code ? await this.#applyDiscount(code, sections) : await this.#fetchCart();

      await this.#notifyCart(cart, cart?.sections ?? data.sections);
      await this.#openCart();
    } catch {
      this.#setStatus(this.dataset.errorMessage);
    } finally {
      this.#setBusy(false);
    }
  };

  #buildCartItems({ id, sellingPlan }, quantity, free) {
    const withSubscription = this.#autoship?.checked && sellingPlan;
    if (!withSubscription) return [{ id, quantity }];

    const paid = { id, quantity: quantity - free, selling_plan: sellingPlan };
    return free > 0 ? [paid, { id, quantity: free }] : [paid];
  }

  #cartSectionIds() {
    return [...document.querySelectorAll('cart-items-component')]
      .map((element) => element.dataset.sectionId)
      .filter(Boolean);
  }

  #fetchCart() {
    return fetch(`${Theme.routes.cart_url}.js`, { headers: { Accept: 'application/json' } })
      .then((response) => response.json())
      .catch(() => null);
  }

  #updateDiscount(discount, sections = []) {
    return fetch(`${Theme.routes.cart_update_url}.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        discount,
        ...(sections.length && { sections: sections.join(',') }),
      }),
    });
  }

  async #clearDiscounts() {
    const cart = await this.#fetchCart();
    if (!cart?.discount_codes?.length) return;

    await this.#updateDiscount('').catch(() => null);
  }

  #applyDiscount(code, sections) {
    return this.#updateDiscount(code, sections)
      .then((response) => (response.ok ? response.json() : this.#fetchCart()))
      .catch(() => this.#fetchCart());
  }

  async #notifyCart(cart, sections) {
    const { CartAddEvent } = await import('@theme/events').catch(() => ({}));
    if (!CartAddEvent) return;

    document.dispatchEvent(
      new CartAddEvent(cart ?? {}, this.id, {
        source: 'bogos-selector',
        itemCount: cart?.item_count ?? 0,
        sections,
      })
    );
  }

  async #openCart() {
    const kaching = window.kachingCartApi;

    if (kaching?.openCart) {
      await kaching.refreshCart?.();
      kaching.openCart();
      return;
    }

    document.querySelector('cart-drawer-component')?.open?.();
  }

  #setBusy(isBusy) {
    this.#button.setAttribute('aria-busy', String(isBusy));
  }

  #setStatus(message) {
    if (this.#status) this.#status.textContent = message;
  }
}

if (!customElements.get('bogos-selector')) {
  customElements.define('bogos-selector', BogosSelector);
}
